import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { StoryCard } from "@/components/StoryCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Button } from "@/components/ui/button";
import { TOPIC_PAGE_CONFIG, getTopicStories } from "@/utils/topicPages";

export const TopicLandingPage = ({ feed, topicKey }) => {
  const topic = TOPIC_PAGE_CONFIG[topicKey];
  const stories = getTopicStories(feed, topicKey);
  const featuredStory = stories[0];

  usePageSeo({
    canonicalPath: topic.route,
    description: topic.description,
    title: `${topic.title} | Content Watch HQ`,
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid={`topic-page-${topic.key}`}>
      <section className="grid gap-8 border border-[rgba(10,15,26,0.12)] bg-white p-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:p-8" data-testid={`topic-page-hero-${topic.key}`}>
        <div>
          <p className="eyebrow text-red-600" data-testid={`topic-page-overline-${topic.key}`}>{topic.eyebrow}</p>
          <h1 className="headline-lg mt-3" data-testid={`topic-page-title-${topic.key}`}>{topic.title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700" data-testid={`topic-page-description-${topic.key}`}>{topic.description}</p>
          <p className="mt-4 max-w-2xl border-l-2 border-red-500 pl-4 text-sm font-semibold uppercase tracking-[0.16em] text-slate-700" data-testid={`topic-page-hero-copy-${topic.key}`}>{topic.heroCopy}</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid={`topic-page-search-targets-${topic.key}`}>
            {topic.searchTargets.map((target) => (
              <span key={target} className="border border-[rgba(10,15,26,0.12)] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700" data-testid={`topic-page-search-target-${topic.key}-${target.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {target}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-none bg-[#D21404] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid={`topic-page-breaking-link-${topic.key}`}>
              <Link to="/breaking-news">
                Back to Breaking News
                <ArrowRight size={16} weight="bold" />
              </Link>
            </Button>
          </div>
        </div>
        {featuredStory ? (
          <VerticalVideoFrame compact story={featuredStory} />
        ) : (
          <div className="border border-[rgba(10,15,26,0.12)] bg-slate-50 p-6" data-testid={`topic-page-empty-hero-${topic.key}`}>
            <p className="eyebrow text-red-600">Waiting for updates</p>
            <p className="mt-3 text-sm leading-7 text-slate-700">As Content Watch publishes more stories matching this Houston topic, they’ll appear here automatically.</p>
          </div>
        )}
      </section>

      <section className="news-card-grid mt-8" data-testid={`topic-page-grid-${topic.key}`}>
        {stories.length ? stories.map((story) => <StoryCard key={story.id} story={story} />) : (
          <div className="border border-[rgba(10,15,26,0.12)] bg-white p-8 md:col-span-2 xl:col-span-3" data-testid={`topic-page-empty-state-${topic.key}`}>
            <p className="eyebrow text-red-600">No stories yet</p>
            <p className="mt-3 text-base leading-8 text-slate-700">This page is ready for Houston search traffic. As matching stories arrive, they’ll start filling this topic page automatically.</p>
          </div>
        )}
      </section>
    </main>
  );
};